import React from "react";

import { Grid } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import {
  Edit,
  Loading,
  maxLength,
  minLength,
  regex,
  required,
  SimpleForm,
  TextInput,
  Toolbar,
  TopToolbar,
  useGetOne,
} from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { BackButton } from "../../../components/common/BackButton";
import {
  CarrierIdentifiersField,
  CarrierIdentifiersInput,
} from "../../../components/carrier/CarrierIdentifiersInput";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { AclType, REGEX_PHONE } from "../../../utils/constants";
import { getBackPath } from "../../../utils/utils";
import { COMPANY_PATH, DASHBOARD_PATH } from "../../carrierPaths";
import { CompanySaveButton } from "./CompanySaveButton";

/**
 * The carrier's own company profile: business name, contact details and the
 * regulatory numbers the company operates under.
 *
 * These belong to the carrier rather than to the transload. The company knows its
 * USDOT number and its dispatch line; a yard does not, and two yards typing the
 * same company's details in two spellings is what makes the records impossible to
 * match up afterwards. So once a carrier has a dispatch account this is the only
 * form that edits them — the yard's Carriers form goes read-only, which
 * isCarrierProfileSelfManaged decides on both sides.
 *
 * What it edits is the CarrierCompany record, which stands above the per-yard
 * Carrier entries and is the same record whichever transload the session is at.
 * handleCarrierChange carries the name, the numbers and the dispatch phone number
 * back down to every entry, so a carrier working four transloads corrects its
 * details once. The session names a yard entry rather than a company, so the
 * entry is read first for the companyId the stream stamped on it.
 *
 * Reached from the user menu rather than the sidebar: it is filled in once at
 * onboarding and rarely touched again, which is where the menu already keeps the
 * person's own profile.
 */

// react-admin-amplify derives its operation names from the resource name, so
// this is Amplify's own plural of the type — getCarrierCompany,
// listCarrierCompanys, updateCarrierCompany.
const COMPANY_RESOURCE = "carrierCompanys";

const validateRequired = required();
// Required, not merely well-formed: the dispatch line is how a yard reaches the
// carrier about a load already on the way, and the number is also what
// handleCarrierChange mirrors down to every yard entry. The other validators
// pass an empty field, so without required() a profile saved blank stays blank.
const validatePhone = [
  validateRequired,
  regex(REGEX_PHONE, "Must be valid Phone No."),
  minLength(10),
  maxLength(20),
];

// Why the company's address is shown but not editable. It is the address the
// primary dispatch account is read off (isCarrierAdmin), so a dispatcher able to
// change it could make themselves the account colleagues cannot delete. Moving
// it is a support request, not a self-service field.
// A carrier session normally names the company it acts for, but UserContext
// handles that field being absent (it clears the stored key), so a session
// without one is a state this page can be opened in. Rendering `<Edit>` with no
// id sends `getCarrier(id: null)` — a GraphQL validation error and a blank page,
// which tells the person nothing they can act on.
const NO_CARRIER_NOTICE =
  "We could not tell which company this account belongs to. Sign out and back in, or ask the transload to check your account.";

const LOAD_FAILED_NOTICE =
  "We could not load your company profile. Try again in a moment, or ask the transload to check your account.";

const EMAIL_NOTICE =
  "The company address is what identifies your primary account, so it is not editable here. Ask the transload to change it.";

// Shown only while the entry has no company record yet — a carrier whose entry
// predates the company records and has not been written since, or one the stream
// has not caught up with. The save still works: it writes the entry, which is
// what the stream places, so the next visit to this page edits the company and
// this notice is gone.
const UNPLACED_NOTICE = (yardName) =>
  `This is your profile at ${
    yardName || "the selected transload"
  }. Saving it also starts the company record shared across your transloads, so your next visit here updates all of them at once.`;

const CompanyProfileForm = ({ record, readOnly, unplaced, yardName }) => (
  <Grid container spacing={1}>
    {readOnly && (
      <Grid item xs={12}>
        <Alert severity="info">
          Your company profile is kept by your dispatchers with user management
          access.
        </Alert>
      </Grid>
    )}
    {unplaced && !readOnly && (
      <Grid item xs={12}>
        <Alert severity="info">{UNPLACED_NOTICE(yardName)}</Alert>
      </Grid>
    )}
    <Grid item xs={12} md={6}>
      <TextInput
        source="businessName"
        label="Business Name"
        variant="outlined"
        fullWidth
        disabled={readOnly}
        validate={validateRequired}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput
        source="email"
        type="email"
        label="Company Email"
        variant="outlined"
        fullWidth
        disabled
        helperText={EMAIL_NOTICE}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextInput
        source="phone"
        type="tel"
        label="Company Phone"
        variant="outlined"
        fullWidth
        disabled={readOnly}
        validate={validatePhone}
      />
    </Grid>
    <Grid item xs={12}>
      {readOnly ? (
        <CarrierIdentifiersField record={record} />
      ) : (
        <CarrierIdentifiersInput />
      )}
    </Grid>
  </Grid>
);

// The page is reached from the user menu, not from a list, so there is nothing
// behind it in react-admin's terms and its own <ListButton> fallback would send
// the carrier back to this same URL. The dashboard is where the menu was opened
// from, and getBackPath still honours a backPath a linking screen supplied.
// Nothing else react-admin clones into an actions element is forwarded: Button
// spreads what it is handed onto the DOM button, and `data`/`hasList` there are
// React warnings.
const CompanyActions = ({ backPath }) => (
  <TopToolbar>
    <BackButton backPath={backPath} />
  </TopToolbar>
);

const CompanyProfileToolbar = ({
  record,
  readOnly,
  carrierId,
  isCompany,
  backPath,
  ...props
}) => (
  <Toolbar {...props}>
    {/* Left out rather than disabled when the whole form is read-only: there is
        nothing for a save to carry, and a greyed-out Save reads as a broken form
        rather than as somebody else's record. */}
    {!readOnly && (
      <CompanySaveButton
        {...props}
        record={record}
        carrierId={carrierId}
        isCompany={isCompany}
        backPath={backPath}
      />
    )}
  </Toolbar>
);

// The form itself, mounted only once the record to edit is known. Split from the
// component below so the fetch hook is never reached with no id: a hook cannot be
// skipped by an early return, and `carrierId` can arrive later in a session when
// the person switches transloads.
const CompanyProfile = ({
  id,
  resource,
  carrierId,
  readOnly,
  unplaced,
  yardName,
  backPath,
  ...props
}) => (
  <Edit
    {...props}
    id={id}
    resource={resource}
    basePath={COMPANY_PATH}
    title={<ForceTitle title="Company Profile" />}
    mutationMode="pessimistic"
    // No aside and no delete: a carrier cannot archive or remove its own entry
    // at a transload, nor the company record. That stays the transload's, as
    // does the invite. Back is the one action the page does need.
    actions={<CompanyActions backPath={backPath} />}
  >
    {/* Neither the form nor the toolbar is handed a record: SimpleForm clones
        both with the one <Edit> fetched. Reading it here through
        useEditController instead would fetch the record a second time, since
        the two hooks are separate subscriptions to the same query. */}
    <SimpleForm
      variant="outlined"
      submitOnEnter={false}
      toolbar={
        <CompanyProfileToolbar
          readOnly={readOnly}
          carrierId={carrierId}
          // Which record the save is for, said rather than inferred from the
          // `resource` react-admin clones into a toolbar: the two subjects take
          // different columns, and getting it wrong sends a yard entry's columns
          // to the company.
          isCompany={resource === COMPANY_RESOURCE}
          // Save leaves the page the same way Back does — see CompanyActions
          // for why that is the dashboard and not a list.
          backPath={backPath}
        />
      }
    >
      <CompanyProfileForm
        readOnly={readOnly}
        unplaced={unplaced}
        yardName={yardName}
      />
    </SimpleForm>
  </Edit>
);

/**
 * Find the company the session's yard entry belongs to, then edit that.
 *
 * The entry is fetched rather than carried in the session: `companyId` is stamped
 * by the stream a moment after any write, so a value read at sign-in can be older
 * than the record, and the profile is opened rarely enough that a fresh read costs
 * nothing. The entry is also the form's fallback subject while `companyId` is
 * still absent — see UNPLACED_NOTICE.
 */
const CompanyProfileGate = ({
  carrierId,
  readOnly,
  yardName,
  backPath,
  ...props
}) => {
  const { data: entry, loading, error } = useGetOne("carriers", carrierId);

  if (loading) return <Loading />;

  // A failed read, not an empty one: `<Edit>` given the wrong id would report the
  // company as missing instead, which reads as the carrier having no profile.
  if (error || !entry)
    return (
      <>
        <ForceTitle title="Company Profile" />
        <CompanyActions backPath={backPath} />
        <Alert severity="warning">{LOAD_FAILED_NOTICE}</Alert>
      </>
    );

  const companyId = entry.companyId;

  return (
    <CompanyProfile
      {...props}
      id={companyId || carrierId}
      resource={companyId ? COMPANY_RESOURCE : "carriers"}
      carrierId={carrierId}
      readOnly={readOnly}
      unplaced={!companyId}
      yardName={yardName}
      backPath={backPath}
    />
  );
};

export const CompanyProfileEdit = (props) => {
  const { appUser, isAuthorized } = useUser();
  // The carrier the session represents, not an id from the URL: a dispatcher can
  // work for two carriers out of one transload, one junction each, so the route
  // carries no id and the session names which company this is.
  const carrierId = appUser?.carrierId;

  // User Management is the carrier portal's "acts for the company" permission —
  // the same one that opens the Users list. Everyone else sees the profile, so a
  // driver or a dispatcher without it can still read off the company's numbers.
  const readOnly = !isAuthorized(AclType.USERS);

  // basePath is the dashboard rather than this page: see CompanyActions.
  const backPath = getBackPath({ ...props, basePath: DASHBOARD_PATH });

  if (!carrierId)
    return (
      <>
        <ForceTitle title="Company Profile" />
        <CompanyActions backPath={backPath} />
        <Alert severity="warning">{NO_CARRIER_NOTICE}</Alert>
      </>
    );

  return (
    <CompanyProfileGate
      {...props}
      carrierId={carrierId}
      readOnly={readOnly}
      yardName={appUser?.reloadYard?.name}
      backPath={backPath}
    />
  );
};
